import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node';
import React from 'react';
import { json } from '@remix-run/node';
import { Link, useLoaderData, useSearchParams } from '@remix-run/react';
import { buildMeta } from '~/lib/seo';
import { routes } from '~/lib/routes';
import {
  getAllSkills,
  getSkillsByClass,
  getSkillsByType,
  getSkillsBySpecialization,
  getAllSpecializations,
  getSpecializationsByClass,
} from '~/lib/game-data.server';
import type { Skill } from '~/types/game-data';
import { H1, H2 } from '~/components/ui/typography';
import { Container } from '~/components/ui/container';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const classFilter = url.searchParams.get('class');
  const typeFilter = url.searchParams.get('type') as 'physical' | 'magic' | null;
  const specializationFilter = url.searchParams.get('specialization');

  let skills: Skill[] = getAllSkills();

  if (classFilter) {
    skills = getSkillsByClass(classFilter);
  } else if (typeFilter) {
    skills = getSkillsByType(typeFilter);
  } else if (specializationFilter) {
    skills = getSkillsBySpecialization(specializationFilter);
  }

  // Get unique classes for filters
  const allSkills = getAllSkills();
  const classSet = new Set<string>();
  allSkills.forEach((skill) => {
    if (skill.class && skill.class !== 'core') {
      classSet.add(skill.class);
    }
  });
  const classes = Array.from(classSet).sort();

  // Get all specializations for filters
  const specializations = getAllSpecializations();
  const specializationsByClass = getSpecializationsByClass();

  return json({
    skills,
    classes,
    specializations,
    specializationsByClass,
    currentClass: classFilter,
    currentType: typeFilter,
    currentSpecialization: specializationFilter,
  });
};

export const meta: MetaFunction = () =>
  buildMeta({
    title: 'Skills Database',
    description: 'Review class abilities and combat techniques from Lumora.',
  });

export default function SkillsIndexPage() {
  const {
    skills,
    classes,
    specializations,
    specializationsByClass,
    currentClass,
    currentType,
    currentSpecialization,
  } = useLoaderData<typeof loader>();
  const [searchParams] = useSearchParams();

  // Helper function to capitalize class names for display
  const capitalizeClassName = (name: string) => {
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  return (
    <Container size="default" padding="large" className="space-y-8">
      <header className="space-y-3">
        <Link
          to={routes.database.index()}
          className="inline-flex items-center text-sm font-semibold text-primary hover:underline"
        >
          ← Back to Database
        </Link>
        <H1>Skills Database</H1>
        <p className="text-lg text-muted-foreground">
          Browse {skills.length} character abilities and combat techniques.
        </p>
      </header>

      {/* Filters */}
      <div className="rounded-lg border border-border bg-card p-6 shadow">
        <H2 className="mb-4 text-lg">Filter by</H2>
        <div className="space-y-4">
          {/* Wheel Type Filter */}
          <div>
            <h3 className="mb-2 text-sm font-medium text-foreground">Wheel Type</h3>
            <div className="flex flex-wrap gap-2">
              <Link
                to={routes.database.skills()}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                  !currentType && !currentClass && !currentSpecialization
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card text-foreground hover:border-primary hover:bg-primary/10'
                }`}
              >
                All
              </Link>
              <Link
                to={`${routes.database.skills()}?type=physical`}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                  currentType === 'physical'
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card text-foreground hover:border-primary hover:bg-primary/10'
                }`}
              >
                Physical
              </Link>
              <Link
                to={`${routes.database.skills()}?type=magic`}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                  currentType === 'magic'
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card text-foreground hover:border-primary hover:bg-primary/10'
                }`}
              >
                Magic
              </Link>
            </div>
          </div>

          {/* Class Filter */}
          {classes.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-medium text-foreground">Class</h3>
              <div className="flex flex-wrap gap-2">
                {classes.map((className) => (
                  <Link
                    key={className}
                    to={`${routes.database.skills()}?class=${className.toLowerCase()}`}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                      currentClass === className.toLowerCase()
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-foreground hover:border-primary hover:bg-primary/10'
                    }`}
                  >
                    {capitalizeClassName(className)}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Specialization Filter */}
          {Object.keys(specializationsByClass).length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-medium text-foreground">Specialization</h3>
              <div className="flex flex-wrap gap-x-8 gap-y-8">
                {Object.entries(specializationsByClass).map(([className, classSpecializations]) => (
                  <div key={className} className="space-y-2 flex-shrink-0">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {capitalizeClassName(className)}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {classSpecializations.map((specialization) => (
                        <Link
                          key={specialization}
                          to={`${routes.database.skills()}?specialization=${encodeURIComponent(specialization)}`}
                          className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                            currentSpecialization === specialization
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border bg-card text-foreground hover:border-primary hover:bg-primary/10'
                          }`}
                        >
                          {specialization}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Skills List */}
      {skills.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-12 text-center">
          <p className="text-lg text-muted-foreground">
            No skills found with the selected filters.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {skills.map((skill) => (
            <Link
              key={skill.id}
              to={routes.database.skills(skill.id)}
              className="group block rounded-lg border border-border bg-card p-6 shadow transition hover:border-primary/30 hover:shadow-lg"
            >
              <div className="mb-2 flex items-center gap-2">
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    skill.wheel_type === 'physical'
                      ? 'bg-orange-500/10 text-orange-500'
                      : 'bg-blue-500/10 text-blue-500'
                  }`}
                >
                  {skill.wheel_type === 'physical' ? 'Physical' : 'Magic'}
                </span>
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                  {skill.skill_category}
                </span>
              </div>
              <h3 className="text-xl font-semibold text-foreground">{skill.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                {skill.description}
              </p>
              <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <span>Class: {capitalizeClassName(skill.class)}</span>
                {skill.cooldown > 0 && (
                  <>
                    <span>•</span>
                    <span>Cooldown: {skill.cooldown}s</span>
                  </>
                )}
              </div>
              <span className="mt-3 inline-flex items-center text-sm font-semibold text-primary transition group-hover:translate-x-1">
                View Details →
              </span>
            </Link>
          ))}
        </div>
      )}
    </Container>
  );
}
